import React from 'react';
import PageShell from '../../components/PageShell';
import {
  Eyebrow, SectionHeading, IconBadge, LetterBadge,
  GradientButton, CTABanner,
} from '../../components/common/Primitives';
import { PARTNERSHIP_MODELS as PM } from '../../lib/mockPartners';
import { Plus, ArrowRight } from 'lucide-react';

export default function PartnershipModels() {
  return (
    <PageShell>
      {/* ================ HERO ================ */}
      <section className="px-6 pt-12 pb-8" data-testid="partnership-models-hero">
        <div className="mx-auto max-w-[1240px]">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_1fr] items-start">
            <div>
              <Eyebrow color="purple">{PM.hero.eyebrow}</Eyebrow>
              <h1 className="mt-3 text-[40px] md:text-[52px] font-extrabold leading-[1.06] tracking-tight text-navy">{PM.hero.title}</h1>
              <p className="mt-5 max-w-xl text-[15.5px] leading-relaxed text-ink-500">{PM.hero.desc}</p>
              <div className="mt-6">
                <GradientButton to={PM.hero.ctaPrimary.to} data-testid="partnership-models-hero-cta">{PM.hero.ctaPrimary.label}</GradientButton>
              </div>
            </div>
            <div className="rounded-3xl border border-canvas-200 bg-gradient-to-br from-purple-50 via-white to-mint-50 p-6 shadow-soft">
              <div className="flex items-center justify-between gap-3">
                {PM.hero.flow.map((f, i) => (
                  <React.Fragment key={f}>
                    <div className="flex-1 rounded-2xl bg-white border border-canvas-200 py-6 text-center shadow-soft">
                      <div className={`mx-auto inline-flex h-9 w-9 items-center justify-center rounded-full text-white text-[13px] font-extrabold ${['bg-purple','bg-royal','bg-cyan'][i]}`}>{i+1}</div>
                      <div className="mt-3 text-[13px] font-extrabold tracking-[0.14em] text-navy">{f}</div>
                    </div>
                    {i < PM.hero.flow.length - 1 && <Plus className="h-5 w-5 shrink-0 text-ink-400" />}
                  </React.Fragment>
                ))}
              </div>
              <p className="mt-5 text-[12.5px] leading-relaxed text-ink-500">{PM.hero.flowNote}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ================ FOUR PARTNERSHIP MODELS ================ */}
      <section className="px-6 py-16" data-testid="partnership-models-four">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={PM.models.eyebrow} title={PM.models.title} desc={PM.models.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PM.models.items.map((m, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft transition-transform hover:-translate-y-0.5 hover:shadow-card flex flex-col">
                <LetterBadge letter={m.letter} color={m.color} />
                <div className="mt-3 text-[15px] font-bold text-navy leading-snug">{m.title}</div>
                <p className="mt-1.5 text-[13px] leading-relaxed text-ink-500 flex-1">{m.desc}</p>
                <div className={`mt-4 inline-flex w-fit rounded-full px-2.5 py-1 text-[10.5px] font-extrabold tracking-[0.12em] bg-${m.color}-50 text-${m.color}`}>{m.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================ ROLE CLARITY TABLE ================ */}
      <section className="px-6 py-16 bg-white" data-testid="partnership-models-roles">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={PM.roles.eyebrow} title={PM.roles.title} desc={PM.roles.desc} />
          <div className="mt-8 overflow-hidden rounded-2xl border border-canvas-200 shadow-soft">
            <div className="hidden md:grid md:grid-cols-4 bg-canvas-100 text-[11.5px] font-bold tracking-[0.12em] text-purple">
              {PM.roles.headers.map((h, i) => (
                <div key={i} className="px-5 py-4 border-r border-canvas-200 last:border-r-0">{h.toUpperCase()}</div>
              ))}
            </div>
            {PM.roles.rows.map((row, r) => (
              <div key={r} className="grid grid-cols-1 md:grid-cols-4 border-t border-canvas-200 bg-white text-[13.5px]">
                {row.map((cell, c) => (
                  <div key={c} className={`px-5 py-4 md:border-r md:border-canvas-200 last:border-r-0 ${c === 0 ? 'font-bold text-navy' : 'text-ink-500 leading-relaxed'}`}>
                    <span className="md:hidden text-[10.5px] font-bold tracking-[0.12em] text-purple block mb-1">{PM.roles.headers[c].toUpperCase()}</span>
                    {cell}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================ MINIMUM DEPLOYMENT STANDARD ================ */}
      <section className="px-6 py-16" data-testid="partnership-models-standard">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={PM.standard.eyebrow} title={PM.standard.title} desc={PM.standard.desc} />
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {PM.standard.items.map((s, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <div className={`text-[28px] font-extrabold leading-none text-${s.color}`}>{s.k}</div>
                <div className="mt-2 text-[13px] font-bold text-navy">{s.label}</div>
                <p className="mt-1.5 text-[12px] leading-relaxed text-ink-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================ IMPLEMENTATION PATHWAY ================ */}
      <section className="px-6 py-16 bg-white" data-testid="partnership-models-pathway">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={PM.pathway.eyebrow} title={PM.pathway.title} desc={PM.pathway.desc} />
          <div className="mt-8 hidden lg:flex items-stretch gap-3">
            {PM.pathway.steps.map((st, i) => (
              <React.Fragment key={i}>
                <div className="flex-1 rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                  <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full bg-${st.color} text-white text-[13px] font-extrabold`}>{st.n}</span>
                  <div className="mt-3 text-[14.5px] font-bold text-navy leading-snug">{st.title}</div>
                  <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-500">{st.desc}</p>
                </div>
                {i < PM.pathway.steps.length - 1 && (
                  <ArrowRight className="h-5 w-5 self-center shrink-0 text-ink-400" />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:hidden">
            {PM.pathway.steps.map((st, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full bg-${st.color} text-white text-[13px] font-extrabold`}>{st.n}</span>
                <div className="mt-3 text-[14.5px] font-bold text-navy leading-snug">{st.title}</div>
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-500">{st.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================ MONITORING AND EVIDENCE ================ */}
      <section className="px-6 py-16" data-testid="partnership-models-monitoring">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={PM.monitoring.eyebrow} title={PM.monitoring.title} desc={PM.monitoring.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PM.monitoring.items.map((m, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <LetterBadge letter={m.letter} color={m.color} />
                <div className="mt-3 text-[14.5px] font-bold text-navy leading-snug">{m.title}</div>
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-500">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================ FINAL CTA ================ */}
      <section className="px-6 pb-24" data-testid="partnership-models-cta">
        <div className="mx-auto max-w-[1240px]">
          <CTABanner title={PM.cta.title} desc={PM.cta.desc} primary={PM.cta.primary} />
        </div>
      </section>
    </PageShell>
  );
}
